/**
 * WHERE THE THREE SUBSYSTEMS MEET. Four props and no logic.
 * =======================================================
 *
 * `FormRenderer` is the forms module's paginated schema renderer, and it takes four seams as props.
 * This file supplies all four with the real implementations and does nothing else — deliberately, so
 * that "what is wired to what" is a page you can read rather than something to trace:
 *
 *   `PhotoCapture`  → `PhotoCaptureField`  → `src/capture`'s `PhotoCapture`
 *   `SerialCapture` → `SerialCaptureField` → `src/capture`'s `SerialScanner`
 *   `storePhoto`    → `capturePhoto`       → `src/core/blobs.putBlob`
 *   `submit`        → passed in            → `enqueue('submission', …)`, see `submission.ts`
 *
 * **The placeholders are gone.** `FieldControl` defaults its two capture props to
 * `PlaceholderPhotoCapture` / `PlaceholderSerialCapture`; because both are supplied here, neither
 * default is ever reached in the app. A test asserts that (`test/ui/composition.test.ts`), because "we
 * passed the real ones" is exactly the sort of claim that rots when a prop gets renamed.
 *
 * `storePhoto` is wired even though the real `PhotoCapture` does not call it — it writes through its own
 * `BlobSink` and hands back an already-stored `PhotoAttachment`. It is wired to the real
 * `capturePhoto` rather than to a throwing stub so that a future capture component which *does* hand
 * over raw bytes works without a change here. See `capture-fields.tsx` for why calling both would store
 * the bytes twice.
 *
 * No hooks. The prefill bundle is rebuilt on every render rather than memoised, which is free: the
 * engine's `useReducer` initialiser runs once, so a fresh bundle object after the first render has no
 * effect on state. Being hook-free is what lets a test walk this component's output and read the props
 * it actually handed to `FormRenderer`.
 */

import { deriveContext, FormRenderer, resolvePrefill } from '@/forms';
import type { ContextValue, FormSchema, SubmissionPayload } from '@/forms';
import { capturePhoto } from '@/forms/submit-core';
import { PhotoCaptureField, SerialCaptureField } from './capture-fields';
import type { InterventionListItem } from '@/core/api';
import type { KioskContext } from './flow';
import { deviceInputs } from './device';
import { prefillInputsFromIntervention } from './intervention';
import { prefillInputsFromKiosk } from './kiosk';
import { normalisePicklistPrefills } from './prefill-normalise';

export interface FormHostProps {
  readonly kiosk: KioskContext;
  readonly schema: FormSchema;
  readonly submit: (payload: SubmissionPayload) => Promise<void>;
  /**
   * The job this visit is against, when the technician arrived from the work list.
   *
   * Optional because the manual-KID path is a real path (PRD §4.3 demotes it to a fallback rather
   * than deleting it) and because a site survey may have no dispatch at all. Absent means the
   * `intervention.*` questions are asked, which is what happens today.
   */
  readonly intervention?: InterventionListItem | null;
  /**
   * When the work list that produced {@link intervention} was read, as an epoch millisecond.
   *
   * Supplied separately because `InterventionListPage._meta` carries `droppedByStateFilter` and
   * `recoveryFlaggedCount` and **no read time** — unlike `KioskPayload._meta.retrievedAt`. Without
   * it, an intervention-only prefill would be stamped with the render time, and a technician who
   * picked a job and then spent twenty minutes driving would see a provenance line claiming the
   * record was read when they opened the form. Ignored when a kiosk payload is present, whose own
   * `retrievedAt` is preferred.
   */
  readonly interventionFetchedAt?: number;
}

export function FormHost({
  kiosk,
  schema,
  submit,
  intervention = null,
  interventionFetchedAt,
}: FormHostProps): JSX.Element {
  /**
   * Device-asserted values FIRST, then the intervention, then the kiosk record on top. **The
   * ordering is load-bearing now that it is no longer decorative**, because two keys are supplied
   * by both middle and last:
   *
   *  · `kiosk.id` — the intervention's `Kiosk_ID__c` is a formula that dereferences the kiosk
   *    record's own `Kiosk_ID__c`, so a collision is the same value; if it ever is not, the record
   *    we actually read is the one to believe.
   *  · `case.number` — `Case__r.CaseNumber` off the intervention (26.70%) versus off the kiosk's
   *    `Case__r` subselect (100%). Kiosk last means the better-filled source wins whenever it is
   *    there, and the intervention fills the gap when it is not.
   *
   * Recomputing `deviceInputs()` per render is deliberate and free: the engine's `useReducer`
   * initialiser runs once, so only the first bundle is ever adopted — which is what makes the
   * catalog's "device clock at first answer" literally true.
   */
  const rawInputs = {
    ...deviceInputs(),
    ...prefillInputsFromIntervention(intervention),
    ...prefillInputsFromKiosk(kiosk.payload),
  };

  /**
   * Record value shapes → this schema's option spellings, unmappable values dropped (H10). Runs
   * between the merge and `resolvePrefill` so that a `single`-kind field is seeded with one of its
   * own options or not at all — `Intervention_Sub_Type__c` stores `LostNetworkConnection` where the
   * dispatch-reason options say 'Lost Network Connection', and a verbatim seed fails validation as
   * `notAnOption` on a field the technician can see is filled. See `prefill-normalise.ts`.
   *
   * `deriveContext` below deliberately reads the RAW inputs: context values (`ctx.lockerType`,
   * `ctx.mountType`, …) are display-logic inputs compared against the RECORD's vocabulary, not a
   * schema's, and must not shift per schema.
   */
  const inputs = normalisePicklistPrefills(rawInputs, schema);

  /**
   * The moment the record was read, taken from the server's own `_meta.retrievedAt` rather than from
   * this device's clock.
   *
   * It matters because `fetchedAt` travels into the submission on every prefilled answer, and a
   * technician reviewing "from the record, read at …" needs the read time, not the render time. For a
   * payload recalled from this session's memory that timestamp is deliberately the ORIGINAL read —
   * which is the honest answer: the value is as old as it is.
   */
  const parsed = kiosk.payload === null ? Number.NaN : Date.parse(kiosk.payload._meta.retrievedAt);
  const fetchedAt = Number.isFinite(parsed)
    ? parsed
    : // No kiosk payload, so fall back to when the work list was read, and only then to the clock.
      // `Number.isFinite` rather than `??` because a caller passing `NaN` should not poison the
      // provenance line with "Invalid Date".
      Number.isFinite(interventionFetchedAt)
      ? (interventionFetchedAt as number)
      : Date.now();

  const { bundle, unknownKeys } = resolvePrefill(inputs, fetchedAt);

  const context: Record<string, ContextValue> = {
    ...deriveContext(rawInputs),
    kioskId: kiosk.kioskId,
    kioskRecord: kiosk.source,
  };

  return (
    <>
      {/*
        Should be impossible: every key `deviceInputs`, `prefillInputsFromIntervention` and
        `prefillInputsFromKiosk` produce is a catalog key, and a test asserts that for each of the
        three. Rendered rather than swallowed because `resolvePrefill` drops an unknown key
        silently, and a silently dropped prefill is a form quietly asking a question it already
        knew the answer to.
      */}
      {unknownKeys.length > 0 && (
        <div className="banner banner--danger" role="alert">
          <p className="banner__title">Prefill mapping bug</p>
          <p className="banner__body">
            {unknownKeys.length} value(s) from the dispatch or the kiosk record are not in the prefill
            catalog and were dropped: <span className="mono">{unknownKeys.join(', ')}</span>. You will
            be asked those questions. Please report this.
          </p>
        </div>
      )}

      <FormRenderer
        schema={schema}
        prefill={bundle}
        context={context}
        storePhoto={capturePhoto}
        submit={submit}
        PhotoCapture={PhotoCaptureField}
        SerialCapture={SerialCaptureField}
      />
    </>
  );
}
